<template>
  <section class="features container">
    <h2 class="billing-data__title">
      Become a Better Leader, Speaker, & Thinker
    </h2>
    <div class="features__list">
      <div v-for="block in blocks" :key="block.title" class="features__item">
        <div class="features__item-icon">
          <img
            :src="getIcon(block.icon)"
            alt="icon"
            class="features__item-icon-image"
          />
          <img
            :src="getIcon(block.icon + '-dark')"
            alt="icon"
            class="features__item-icon-image-night"
          />
        </div>
        <div class="features__item-text">
          <div class="features__item-title">{{ block.title }}</div>
          <div class="features__item-description">
            {{ block.description }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ContentStats } from '@/helpers/contentStats'

const blocks = [
  {
    title: 'Save Time & Learn Faster',
    description:
      'Our summaries convey the key points without fluff and without losing important info. You will learn 10x faster.',
    icon: 'save-time',
  },
  {
    title: 'Improve Your Decision Making',
    description:
      'Shortform summaries are designed to be actionable.  Apply the lessons directly to your life.',
    icon: 'decisions',
  },
  {
    title: 'Read More Consistently',
    description:
      '1-page and Full summaries make reading more accessible. You can read a new summary every day.',
    icon: 'read-more',
  },
  {
    title: 'Broaden Your Knowledge',
    description: `We cover dozens of topics across over ${ContentStats.books} book summaries, and add new summaries every week.`,
    icon: 'knowledge',
  },
  {
    title: 'Increase Your Retention',
    description:
      'Ever forget an important fact from a book? Our summaries clearly state key information in plain language.',
    icon: 'retention',
  },
]

function getIcon(icon: string): string {
  return require(`@/assets.app/images/billing/${icon}.svg`)
}
</script>

<style scoped lang="scss">
.features {
  gap: 48px;
  &__list {
    display: flex;
    flex-direction: row;
    gap: 60px 32px;
    flex-wrap: wrap;
    justify-content: center;

    @media (max-width: $w-mobile-s) {
      flex-direction: column;
      justify-content: start;
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    width: 300px;

    @media (max-width: $w-mobile-s) {
      flex-direction: row;
      gap: 20px;
      align-items: start;
      width: 100%;
    }

    &-icon {
      flex-shrink: 0;
      @media (max-width: $w-mobile-s) {
        width: 45px;
        height: 45px;
      }

      &-image-night {
        display: none;
      }
    }

    &-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;

      @media (max-width: $w-mobile-s) {
        align-items: start;
        text-align: left;
      }
    }

    &-title {
      color: $c-black-2;
      text-align: center;
      font-size: 24px;
      font-weight: 500;
      line-height: 32px;

      @media (max-width: $w-tablet-s) {
        font-size: 20px;
        line-height: 32px;
      }
      @media (max-width: $w-mobile-s) {
        line-height: 24px;
      }
    }

    &-description {
      color: $c-black-2;
      font-weight: 400;
      text-align: center;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: -0.32px;
      @media (max-width: $w-mobile-s) {
        text-align: left;
      }
    }
  }
}

.night-mode {
  .features {
    &__item-title {
      color: $c-white;
    }
    &__item-description {
      color: $c-black-11;
    }
    &__item-icon-image {
      display: none;
    }

    &__item-icon-image-night {
      display: block;
    }
  }
}
</style>
